import { Button, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmPeriodMatchDialog from 'modules/common/components/VerifiedStatementUpload/components/ConfirmPeriodMatchDialog';
import IncompleteStatementsDialog from 'modules/common/components/VerifiedStatementUpload/components/IncompleteStatementsDialog';
import StatementMonthTooltip from 'modules/common/components/VerifiedStatementUpload/components/StatementMonthTooltip';
import StatementSlotList, {
    StatementFile,
} from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import StatementUploadArea from 'modules/common/components/VerifiedStatementUpload/components/StatementUploadArea';
import earliestUploadedAtMs, { DatedUpload, uploadBatchProgress } from 'modules/common/utils/uploadBatch';
import useProcessingExpiryTick from 'modules/common/components/VerifiedStatementUpload/hooks/useProcessingExpiryTick';
import useStatementUploadVerdicts from 'modules/common/components/VerifiedStatementUpload/hooks/useStatementUploadVerdicts';
import foldStagedAssignments, {
    changedAssignments,
} from 'modules/common/components/VerifiedStatementUpload/utils/foldStagedAssignments';
import heldHereFiles, {
    StatementDocumentsByUuid,
} from 'modules/common/components/VerifiedStatementUpload/utils/heldHereFiles';
import { formatMonthRange } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import {
    includesPeriod,
    periodsNotHeld,
    rowKey,
    sortPeriods,
    statementAccountLabel,
    statementRows,
    StatementRowRef,
} from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { isAwaitingVerdict } from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import Dialog from 'modules/common/components/MUI/Dialog';
import { Document } from 'modules/common/interfaces/documents';
import { StatementPeriodAssignment } from 'services/bookkeeping/mutations/useReassignStatementPeriods';
import {
    AccountStatementCoverage,
    StatementPeriod,
    STATEMENT_COVERAGE_QUERY_KEY,
} from 'services/bookkeeping/queries/useStatementCoverage';

/** `uploadBatchProgress` identifies uploads by `id`; a statement's identity is its document. */
const toDatedUpload = ({ documentUuid, uploadedAt }: StatementFile): DatedUpload => ({
    id: documentUuid,
    uploadedAt,
});

/** A month the member picked, awaiting confirmation because we didn't pick it ourselves. */
interface PendingMatch {
    documentUuid: string;
    fileName: string;
    period: StatementPeriod;
    believedPeriods: StatementPeriod[];
    /** The row the pick applies to, carried so confirming stages it against that row. */
    sourcePeriod?: StatementPeriod;
}

interface VerifiedStatementUploadModalProps {
    readonly open: boolean;
    readonly onClose: () => void;
    /** The account card this modal was opened from — sets the expectation for every upload. */
    readonly account: AccountStatementCoverage;
    /**
     * The months to ask for, defaulting to everything the account owes.
     *
     * A caller narrower than the account passes its own: a back-books task asks only for the
     * months its context names, and the account's window reaches back to the books start. Every
     * month given here becomes a slot row with its own delete, and a month withheld is one the
     * member is not asked for and cannot accidentally clear — so this decides exposure, not just
     * labelling. Accounting setup has no narrower ask and takes the default.
     */
    readonly requiredPeriods?: StatementPeriod[];
    /** The files the caller lists for this card — the ones uploaded under it. */
    readonly files: StatementFile[];
    /**
     * Every statement document the member has, not only this card's: a month here can be held by
     * a file uploaded under another card, and the window has to name it.
     */
    readonly documentsByUuid: StatementDocumentsByUuid;
    /**
     * Take a file off this card: one month of it, or every month it holds here when the period is
     * absent. Per card — a file's months on other cards are not this window's to remove.
     */
    readonly onRemove: (documentUuid: string, period?: StatementPeriod) => void | Promise<void>;
    /**
     * Files each settled batch produced, for the caller to file against its own account.
     *
     * The dropzone itself lives here rather than being injected. It used to be a render prop on
     * the theory that the caller owned the uploader and this modal owned the verdicts — but both
     * callers passed character-identical wiring, so the seam bought nothing and cost each of them
     * a copy of every upload limit. What actually differs between them is where a finished batch
     * gets filed, which is this one callback.
     */
    readonly onUploadSuccess: (uploaded?: Document[]) => Promise<void>;
    /**
     * The months already credited to each document, from coverage.
     *
     * A list per document rather than one month, because a single PDF can carry several monthly
     * statements and holds a tracker row for each. Collapsed to one, the extras were invisible
     * here and a correction to any of them dropped the rest.
     */
    readonly heldPeriodsByUuid: Record<string, StatementPeriod[] | undefined>;
    /**
     * Persist every month correction made in this session, as one write.
     *
     * A map rather than a per-row callback because these are staged until Save: one
     * write for the whole modal is what keeps two corrections from racing each other
     * onto a blob that is replaced wholesale. `null` clears a saved correction.
     *
     * Resolves `true` when the write landed, so the modal only closes on success and
     * a member whose save failed keeps their edits to retry.
     */
    readonly onSavePeriods: (assignments: StatementPeriodAssignment[]) => Promise<boolean>;
    /**
     * Confirm before leaving with months still outstanding.
     *
     * **Opt-in, and it must stay opt-in.** Without it Save, Cancel and the backdrop all behave
     * exactly as before, which is what the back-books task drawer depends on: it mounts this
     * modal only while a row is open and relies on unmounting to discard staged corrections, so
     * Cancel and close have to remain one action there. A caller that wants the friction says so.
     *
     * The months it counts are the persisted coverage for this account. A staged correction is
     * still discarded on close, so it must not make the exit look complete before it is saved.
     */
    readonly confirmIncompleteExit?: boolean;
    /**
     * Drop the explicit Save and write this session's corrections on the way out instead.
     *
     * The footer becomes a single Close, and every exit — Close and the backdrop — commits.
     *
     * **Opt-in, because it inverts what an exit means.** Without it, Cancel discards staged
     * corrections and only Save writes them, which is what the back-books drawer relies on: it
     * mounts this modal per row and unmounts to throw the staged map away. Turning it on there
     * would silently start persisting corrections a member backed out of.
     *
     * With it, there is no route that discards, so the surface can honestly tell the member
     * their progress is saved.
     */
    readonly commitOnExit?: boolean;
}

/**
 * Per-account statement upload with real-time verification.
 *
 * Files are judged against `account`, not against the member's whole account set — a
 * statement for a different account is rejected here rather than silently re-filed.
 *
 * Save is gated only on nothing being in flight, not on the ask being complete: a member
 * who can only find four of seven months must still be able to move on.
 *
 * Uploads and deletes persist as they happen — they are irreversible acts on a file, and a
 * member who uploads then closes must not lose it. Month corrections are the opposite: they
 * are edits to how we read a file we already hold, cheap to revise while the modal is open,
 * so they stage and are written once on Save. That split is also what makes Cancel
 * meaningful — there is something to discard.
 */
export default function VerifiedStatementUploadModal({
    open,
    onClose,
    account,
    files,
    requiredPeriods: requiredPeriodsProp = undefined,
    documentsByUuid,
    onRemove,
    onUploadSuccess,
    heldPeriodsByUuid,
    onSavePeriods,
    confirmIncompleteExit = false,
    commitOnExit = false,
}: Readonly<VerifiedStatementUploadModalProps>) {
    const queryClient = useQueryClient();
    /** The member's pick, held while they confirm it. Null when nothing is being asked. */
    const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null);
    /**
     * The way out the member asked for, held while they confirm leaving months outstanding.
     *
     * The intent is kept rather than collapsed to a single "close", because Save and Cancel do
     * different things and "Submit anyway" has to resume the one that was actually requested — a
     * member who hit Save and confirmed must get their corrections written, not discarded.
     */
    const [pendingExit, setPendingExit] = useState<'save' | 'close' | null>(null);
    /**
     * Corrections made in this session but not yet written, as an overlay on what is saved.
     *
     * Each entry carries both the intent and the row it was made on. One state rather than two
     * parallel maps because every write sets both, and nothing would enforce that they stay in
     * step. The row is kept because the row list below is built from coverage, which can lag the
     * statuses endpoint the member is correcting against — so a staged key is not guaranteed to
     * appear there, and the request must never have to parse one back out of a key.
     *
     * Keyed by row, not by document: a file carrying several statements has a row per month, and
     * a correction is made on one of them. A `null` period is an explicit "clear the saved one",
     * which an absent key cannot express.
     */
    const [staged, setStaged] = useState<Record<string, { row: StatementRowRef; period: StatementPeriod | null }>>({});
    const [isSaving, setIsSaving] = useState(false);
    /**
     * Whether a commit is in flight, held on a ref rather than read off `isSaving`.
     *
     * `isSaving` disables the footer button, which is all it can do: the backdrop still routes
     * through `onClose` and is never disabled, so a member can ask to exit again while the
     * first write is still open. State would not stop it even if it were — `setIsSaving(true)`
     * happens a microtask after the click that caused it, so the second exit reads the render
     * before it. A ref flips synchronously and is what the second exit actually sees.
     */
    const isCommittingRef = useRef(false);
    /**
     * Whether a batch is still on its way up.
     *
     * The gate below cannot be built from `files` alone. Those are server documents, and during
     * the transfer none of them exists yet — so `awaitingCount` reads zero, Save stays live, and
     * clicking it closes the modal, unmounts the uploader and takes the transfer down with it.
     * The member loses the upload they are watching, to a button that looks safe.
     *
     * Owned by the uploader rather than inferred here, because the transfer is the one stage of
     * this flow the server cannot report on, and it stays true across the settle — until the
     * refetch has caught up — which is the second, shorter window with the same failure.
     */
    const [isUploading, setIsUploading] = useState(false);

    // Resolved once: the verdicts, the header range and the slot rows must all describe the
    // same ask, and three separate fallbacks are three chances for them to disagree.
    const askedPeriods = requiredPeriodsProp ?? account.required_periods;

    /** Files shown only because they hold one of this window's months. Never counted as in flight. */
    const heldHere = useMemo(
        () => heldHereFiles({ files, heldPeriods: account.held_periods, askedPeriods, documentsByUuid }),
        [files, account.held_periods, askedPeriods, documentsByUuid]
    );
    const heldHereUuids = useMemo(() => heldHere.map((file) => file.documentUuid), [heldHere]);
    /** Everything the window shows a row for. */
    const windowFiles = useMemo(() => [...files, ...heldHere], [files, heldHere]);
    const documentUuids = useMemo(() => windowFiles.map((file) => file.documentUuid), [windowFiles]);

    /**
     * Every row a correction can be made on, and where each stands before this session's edits.
     *
     * Built together because the second is a projection of the first, keyed the same way — two
     * memos over one input meant two dep lists and two places building the key.
     *
     * A saved month is the row's own month: a credited row is already "assigned" as far as the
     * display is concerned, because coverage cannot say whether we inferred it or the member
     * corrected it. What that buys is a baseline the fold can diff against, so an untouched row
     * is never sent.
     */
    const { rows, rowByKey, savedByRowKey } = useMemo(() => {
        const statementRowList = statementRows(documentUuids, heldPeriodsByUuid);
        const byKey = new Map<string, StatementRowRef>();
        const saved: Record<string, StatementPeriod | undefined> = {};
        statementRowList.forEach((row) => {
            const key = rowKey(row.documentUuid, row.period);
            byKey.set(key, row);
            if (row.period) {
                saved[key] = row.period;
            }
        });
        return { rows: statementRowList, rowByKey: byKey, savedByRowKey: saved };
    }, [documentUuids, heldPeriodsByUuid]);

    /**
     * What the rows should show right now: saved corrections with this session's
     * staged ones laid over them.
     *
     * The display is driven from this rather than from what is persisted, so the
     * member sees the consequence of a correction — including which file it
     * displaces — before committing to it. `null` means "clear the saved one",
     * which flattens to no assignment here.
     */
    /**
     * This session's corrections, re-pointed at the row each one now applies to.
     *
     * A correction staged while coverage reported no row for a file keys on the file itself. If a
     * row for it then appears — an ingest webhook re-delivering and winning the month, which its
     * `already_tracked` guard cannot catch while there is no row — the two keys would coexist and
     * the file would be credited for its own month *and* the member's, on one pick. Re-pointing
     * keeps the pick and collapses it onto the row it belongs to; dropping it would lose the
     * member's action silently instead.
     *
     * Insertion order is preserved, because the fold reads it as edit order.
     */
    const stagedRowEntries = useMemo(
        () =>
            Object.values(staged).map(({ row, period }) => {
                const held = heldPeriodsByUuid[row.documentUuid] ?? [];
                if (row.period || held.length === 0) {
                    return { row, period };
                }
                // Sorted, so this names the same row `statementRows` calls first. `held_periods`
                // arrives in whatever order the API sends it, and every other definition of a
                // file's first row sorts — an unsorted pick here would move a different row than
                // the tick and the caption describe.
                return { row: { documentUuid: row.documentUuid, period: sortPeriods(held)[0] }, period };
            }),
        [staged, heldPeriodsByUuid]
    );

    const stagedPeriods = useMemo(
        () =>
            Object.fromEntries(
                stagedRowEntries.map(({ row, period }) => [rowKey(row.documentUuid, row.period), period])
            ),
        [stagedRowEntries]
    );

    /**
     * Every row a correction can name: what coverage reports, plus the rows staged against.
     *
     * One list for the fold and the resolver both, because they key on it identically — derived
     * separately, a correction made while coverage lagged the statuses read looked up under a key
     * the other had never produced, and every credited month rendered as missing.
     */
    const addressableRows = useMemo(() => {
        // Deduplicated by key: a correction is normally staged against a row coverage already
        // reports, so the two lists overlap. Left doubled, a file's row count reads as two and it
        // is treated as spanning months — which withholds menu entries and relabels every control.
        const byKey = new Map<string, StatementRowRef>();
        [...rows, ...stagedRowEntries.map((entry) => entry.row)].forEach((row) => {
            byKey.set(rowKey(row.documentUuid, row.period), row);
        });
        return [...byKey.values()];
    }, [rows, stagedRowEntries]);

    const stagedRowByKey = useMemo(
        () => new Map(stagedRowEntries.map(({ row }) => [rowKey(row.documentUuid, row.period), row])),
        [stagedRowEntries]
    );

    const assignmentByRowKey = useMemo(
        () =>
            foldStagedAssignments({
                saved: savedByRowKey,
                staged: stagedPeriods,
                rows: addressableRows,
                documentUuids,
            }),
        [savedByRowKey, stagedPeriods, addressableRows, documentUuids]
    );

    const { verdictByUuid, ownerByPeriodKey, holdingsByUuid, isAllSettled } = useStatementUploadVerdicts({
        documentUuids,
        bankAccountId: account.bank_account_id,
        heldHereUuids,
        documentsByUuid,
        requiredPeriods: askedPeriods,
        assignmentByRowKey,
        rows: addressableRows,
        enabled: open,
    });

    /**
     * The asked months not yet persisted for this account — what the member would be leaving behind.
     *
     * This intentionally differs from `ownerByPeriodKey`, which includes staged corrections so the
     * member can preview Save. Close discards those corrections, so using that live map here could
     * let a staged final assignment bypass the confirmation and then disappear.
     */
    const outstandingPeriods = periodsNotHeld(askedPeriods, account.held_periods);

    /**
     * Wake up when the oldest unanswered upload ages out.
     *
     * The two clock reads below and in `StatementSlotList` are only as fresh as the last render,
     * and a file the server never answers for is precisely the case where no render is coming.
     * Scheduled here rather than in the list because the count below needs the same fresh
     * reading, and this component renders the list — one timer serves both.
     */
    const uploadedAts = useMemo(() => files.map((file) => file.uploadedAt), [files]);
    useProcessingExpiryTick(uploadedAts);

    /**
     * How many files are genuinely still in flight — the figure the dropzone reports, and the
     * Save gate.
     *
     * Deliberately not memoised, and deliberately not the hook's `isAnyProcessing`: this reads a
     * clock through `isAwaitingVerdict`, and a document the server never answered for stays
     * `processing` by kind forever. Counting those would leave the dropzone spinning over its
     * own upload prompt and Save disabled for the rest of the session, while the row for that
     * same file asks the member to delete it.
     */
    const awaitingFiles = files.filter((file) => isAwaitingVerdict(verdictByUuid[file.documentUuid], file.uploadedAt));
    const awaitingCount = awaitingFiles.length;

    /**
     * How far the server has got through the batch, for the dropzone's narration.
     *
     * Derived from the files on every render rather than remembered, so it survives this modal
     * closing and reopening — state here would be reset by the very act it needs to survive.
     */
    const { processedCount } = uploadBatchProgress(files.map(toDatedUpload), awaitingFiles.map(toDatedUpload));

    /**
     * When the work now in flight began, for the screen's timed opening.
     *
     * Read off the files rather than remembered in state, so it survives this modal closing and
     * reopening — which is the whole point. State here would be reset by the very act the anchor
     * exists to survive.
     */
    const awaitingSinceMs = earliestUploadedAtMs(awaitingFiles);

    /**
     * Whether a batch has actually been watched in flight while this modal was open.
     *
     * The settle below is the same condition either way — nothing left processing — but it means
     * two different things: a batch finishing, or a modal opening on statements that were read
     * yesterday. Only the first is "finalizing", and only the files tell them apart.
     */
    const hasWatchedBatchRef = useRef(false);
    useEffect(() => {
        if (awaitingCount > 0) {
            hasWatchedBatchRef.current = true;
        }
    }, [awaitingCount]);

    /**
     * True from the last verdict landing until the refresh it triggers has come back.
     *
     * This is the whole of "Finalizing": every statement has been read and what is left is our
     * own wrap-up. It is deliberately not a duration — the screen used to reach this line ten
     * seconds after mount whatever the server was doing, which is how a member came to be told
     * "Almost done" over a batch that had not been started.
     */
    const [isFinalizing, setIsFinalizing] = useState(false);

    /**
     * Refresh coverage once the batch has settled.
     *
     * Coverage backs the cards behind this modal and the Continue gate, and a credited file
     * changes what they show — but it is a live Tight plus Plaid round trip, so it is asked
     * once, at the only moment the answer for the whole batch can have changed.
     *
     * Only fires for a member still watching; the section covers the other path by refetching
     * in its own close handler. Every dependency is stable, so this re-runs only when settling
     * genuinely flips — the ref below is not there to stop it re-running, but to tell the two
     * ways of arriving at "settled" apart.
     */
    useEffect(() => {
        if (!open || !isAllSettled) {
            return;
        }
        // Read and cleared together: this is the one commit where the flag means "the batch the
        // member just watched has finished", and leaving it set would make the next open of a
        // long-settled modal claim a batch had landed.
        const hasBatchJustLanded = hasWatchedBatchRef.current;
        hasWatchedBatchRef.current = false;
        if (hasBatchJustLanded) {
            setIsFinalizing(true);
        }
        // `cancelRefetch: false` because the default is TRUE: without it this would abort an
        // in-flight coverage fetch. The request layer never threads the abort signal into
        // axios, so the server pays for that round trip anyway and its response is discarded.
        //
        // Awaited for the narration: the promise settles when the refetch does, success or
        // failure, so the overlay comes down when the work behind it is actually over rather
        // than after a guessed interval.
        queryClient
            .invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY }, { cancelRefetch: false })
            .finally(() => setIsFinalizing(false));
    }, [open, isAllSettled, queryClient]);

    const rangeLabel = useMemo(() => formatMonthRange(askedPeriods), [askedPeriods]);
    /**
     * The account as prose, for the sentence above the dropzone and for the incomplete-exit
     * prompt. Shared rather than composed here: step completion raises that same prompt with
     * its own label, and two spellings of one account read as two accounts.
     */
    const accountLabel = statementAccountLabel(account);

    /**
     * Record what the member did to one row, and nothing else.
     *
     * Displacement is deliberately NOT written here. Storing it as a side effect meant a
     * staged `null` outlived the pick that caused it: delete the file that took a month and
     * the batch still carried "take it off the other one", silently reverting a correction
     * the member never touched. Intent is stored; the consequence is derived, once, below.
     */
    const stagePeriod = useCallback(
        (documentUuid: string, sourcePeriod: StatementPeriod | undefined, targetPeriod: StatementPeriod | null) => {
            const row = { documentUuid, period: sourcePeriod };
            const key = rowKey(documentUuid, sourcePeriod);
            setStaged((current) => {
                // Re-inserted rather than updated in place. `foldStagedAssignments` gives a
                // contested month to whichever entry it reaches last, and a spread over an
                // existing key leaves that key where it was — so correcting one file, then a
                // second onto the same month, then the first one back would hand the month to the
                // second: the member's last action losing to the one before it.
                const next = { ...current };
                delete next[key];
                next[key] = { row, period: targetPeriod };
                return next;
            });
        },
        []
    );

    /**
     * Take the member's pick, asking first only when it contradicts our own reading.
     *
     * Agreeing with us is staged silently: confirming a choice we already made would be
     * ceremony with nothing behind it, and a prompt the member always dismisses is a prompt
     * they stop reading before the one that matters.
     */
    const handleSelectPeriod = useCallback(
        (documentUuid: string, period: StatementPeriod, sourcePeriod?: StatementPeriod) => {
            const { believedPeriods } = verdictByUuid[documentUuid];
            if (includesPeriod(believedPeriods, period)) {
                stagePeriod(documentUuid, sourcePeriod, period);
                return;
            }
            setPendingMatch({
                documentUuid,
                fileName: windowFiles.find((file) => file.documentUuid === documentUuid)?.fileName ?? 'this file',
                period,
                believedPeriods,
                sourcePeriod,
            });
        },
        [verdictByUuid, windowFiles, stagePeriod]
    );

    /** Undoing a choice restores our own reading, so there is nothing to confirm. */
    const handleClearPeriod = useCallback(
        (documentUuid: string, sourcePeriod?: StatementPeriod) => stagePeriod(documentUuid, sourcePeriod, null),
        [stagePeriod]
    );

    const handleConfirmMatch = useCallback(() => {
        if (!pendingMatch) {
            return;
        }
        setPendingMatch(null);
        stagePeriod(pendingMatch.documentUuid, pendingMatch.sourcePeriod, pendingMatch.period);
    }, [pendingMatch, stagePeriod]);

    const handleCancelMatch = useCallback(() => setPendingMatch(null), []);

    /**
     * Write this session's month corrections, then close.
     *
     * Staged rather than written per click, so two corrections in quick succession cannot
     * race each other onto a blob the server replaces wholesale. Stays open when the write
     * fails, so the member's edits survive to be retried. Nothing resets the staged map on
     * the way out — a caller closes this modal by unmounting it, so Cancel and close are the
     * same action.
     */
    const commitPeriods = useCallback(async () => {
        // Dropped rather than queued: the write already in flight carries these same corrections,
        // and it closes the modal itself when it lands. A second identical write would only buy a
        // second success snackbar and a `finally` that clears the latch out from under the first.
        if (isCommittingRef.current) {
            return;
        }

        // Exactly what the member is looking at — the same fold that drew the rows, so what
        // is saved cannot differ from what was shown.
        const live = changedAssignments(assignmentByRowKey, savedByRowKey);

        // Row keys are resolved back to the row they name rather than parsed, so the request
        // carries the period object coverage gave us instead of one rebuilt from a string.
        const assignments = Object.entries(live).flatMap(([key, targetPeriod]) => {
            // Only the member's own undo goes up blank. A row cleared because another file took
            // its month is the server's to displace, and blank does not mean "unfiled" here — it
            // means "put it back on the month we read", which is the month the other file is
            // claiming. Two claims on one month with only one of them named slips past the
            // server's duplicate check, and whichever applies second deletes the other's row.
            // Tested against `null` rather than absence: a pick displaced by a later pick still
            // holds its month in the staged map.
            if (targetPeriod === null && stagedPeriods[key] !== null) {
                return [];
            }
            // The staged record first: it is the row the member actually acted on. `rowByKey`
            // covers the other case — a saved row displaced by someone else's pick, which the
            // member never touched and so has no staged entry.
            const row = stagedRowByKey.get(key) ?? rowByKey.get(key);
            return row ? [{ documentUuid: row.documentUuid, sourcePeriod: row.period, targetPeriod }] : [];
        });

        if (assignments.length === 0) {
            onClose();
            return;
        }

        isCommittingRef.current = true;
        setIsSaving(true);
        try {
            if (await onSavePeriods(assignments)) {
                onClose();
            }
        } finally {
            isCommittingRef.current = false;
            setIsSaving(false);
        }
    }, [assignmentByRowKey, savedByRowKey, stagedPeriods, stagedRowByKey, rowByKey, onSavePeriods, onClose]);

    /**
     * What an exit actually does, once nothing is left to confirm.
     *
     * Shared by the direct route and the one resumed after the confirmation, so the two cannot
     * disagree about whether corrections get written — which is the bug this shape prevents:
     * stating it twice meant "Submit anyway" could discard edits the direct path would have kept.
     *
     * Under `commitOnExit` every intent commits, which is what makes Close the only button the
     * footer needs.
     */
    const performExit = useCallback(
        async (intent: 'save' | 'close') => {
            if (intent === 'save' || commitOnExit) {
                await commitPeriods();
                return;
            }
            onClose();
        },
        [commitOnExit, commitPeriods, onClose]
    );

    /**
     * One gate on every way out, so no route can bypass it.
     *
     * Save, Cancel and the backdrop all come through here — a guard hung on Cancel alone
     * would be dismissable by clicking the backdrop, which is the easiest exit of the three.
     */
    const requestExit = useCallback(
        async (intent: 'save' | 'close') => {
            if (confirmIncompleteExit && outstandingPeriods.length > 0) {
                setPendingExit(intent);
                return;
            }
            await performExit(intent);
        },
        [confirmIncompleteExit, outstandingPeriods.length, performExit]
    );

    /** Resume the exit the member originally asked for, not a generic close. */
    const handleSubmitAnyway = useCallback(async () => {
        const intent = pendingExit;
        setPendingExit(null);
        if (intent) {
            await performExit(intent);
        }
    }, [pendingExit, performExit]);

    /**
     * Dismiss the confirmation and leave the member where they were.
     *
     * Nothing else is reset: this modal is still mounted behind the confirmation, so the staged
     * corrections and the uploaded files are all exactly as they were left.
     */
    const handleAddMore = () => setPendingExit(null);

    const handleRequestClose = () => requestExit('close');
    const handleSave = () => requestExit('save');

    /**
     * Whether the ask is finished, which is what turns the exit's label from Close into Done.
     *
     * Read off `outstandingPeriods` — persisted coverage — rather than the live assignment map,
     * for the same reason the exit confirmation is: a staged correction is not a held statement
     * yet. Sharing the one source is what keeps the label and the confirmation from contradicting
     * each other, so "Done" can never be followed by "You're missing 1 statement".
     *
     * In-flight work counts against it too. A file still on its way up or still being read might
     * yet change what is held, and a footer saying Done beside a dropzone narrating progress
     * describes a different screen from the one the member is looking at.
     */
    const isComplete = outstandingPeriods.length === 0 && !isUploading && awaitingCount === 0 && !isFinalizing;

    /**
     * One Close where exits commit, Cancel/Save where they don't.
     *
     * The commit-on-exit footer goes through the `actions` slot rather than `positiveAction`,
     * because it has to seat the saved-state note on the same row: the note is what earns the
     * missing Save button, so putting it anywhere else leaves the footer just short of a control.
     *
     * Close is never disabled on `awaitingCount` the way Save was. Save could refuse while a
     * verdict was in flight because Cancel was still there to leave by; as the only way out, a
     * Close that locks itself would trap the member behind a file we are still reading.
     *
     * It does hold for `isUploading`, which is the one window where leaving destroys work rather
     * than deferring it: the transfer lives in the uploader this modal unmounts. That window is
     * bounded by the upload itself, so the member is waiting on their own file, not on us.
     *
     * The one button carries both endings. Close and Done are the same exit — nothing about the
     * write changes — so this is a label, not a branch: a second button would be a second way out
     * of a footer whose whole point is having one.
     */
    const footerActions = commitOnExit
        ? {
              actions: (
                  <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                          Your progress is auto-saved
                      </Typography>
                      <Button
                          variant="outlined"
                          size="large"
                          onClick={handleRequestClose}
                          disabled={isSaving || isUploading}
                          loading={isSaving}>
                          {isComplete ? 'Done' : 'Close'}
                      </Button>
                  </Stack>
              ),
          }
        : {
              negativeAction: { label: 'Cancel', onClick: handleRequestClose, disabled: isSaving },
              positiveAction: {
                  label: 'Save',
                  onClick: handleSave,
                  // Only gate on in-flight work — an incomplete set is still savable. Both
                  // halves of "in flight" are needed: files on their way up, and documents the
                  // server has not answered for. Neither covers the other's window.
                  disabled: isUploading || awaitingCount > 0,
                  loading: isSaving,
              },
          };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleRequestClose}
                heading="Upload bank statements"
                size="medium"
                showHeaderCloseButton={false}
                {...footerActions}>
                <Stack spacing={3}>
                    <Typography variant="body1">
                        {rangeLabel ? (
                            <>
                                {`Upload your statements for ${accountLabel} from `}
                                <strong>{rangeLabel}</strong>. <StatementMonthTooltip />
                            </>
                        ) : (
                            `Upload your statements for ${accountLabel}.`
                        )}
                    </Typography>

                    <Stack spacing={2}>
                        {/* Both counts are derived from a clock read, so they are passed straight
                            through on every render rather than memoised, which would pin them. */}
                        <StatementUploadArea
                            processingCount={awaitingCount}
                            processedCount={processedCount}
                            isFinalizing={isFinalizing}
                            onUploadingChange={setIsUploading}
                            processingSinceMs={awaitingSinceMs}
                            onUploadSuccess={onUploadSuccess}
                        />
                        <StatementSlotList
                            files={windowFiles}
                            verdictByUuid={verdictByUuid}
                            ownerByPeriodKey={ownerByPeriodKey}
                            holdingsByUuid={holdingsByUuid}
                            accountMask={account.account_mask}
                            onRemove={onRemove}
                            requiredPeriods={askedPeriods}
                            heldPeriods={account.held_periods}
                            onSelectPeriod={handleSelectPeriod}
                            onClearPeriod={handleClearPeriod}
                        />
                    </Stack>
                </Stack>
            </Dialog>

            {pendingMatch && (
                <ConfirmPeriodMatchDialog
                    open
                    onClose={handleCancelMatch}
                    onConfirm={handleConfirmMatch}
                    fileName={pendingMatch.fileName}
                    period={pendingMatch.period}
                    believedPeriods={pendingMatch.believedPeriods}
                />
            )}

            {/* Rendered over the still-mounted upload modal, so Add more returns the member to
                their staged corrections and uploaded files untouched. */}
            {pendingExit && (
                <IncompleteStatementsDialog
                    open
                    onAddMore={handleAddMore}
                    onSubmitAnyway={handleSubmitAnyway}
                    accountLabel={accountLabel}
                    // Persisted coverage only — a staged assignment can still be discarded by
                    // this exit, so it must not hide a missing period from the confirmation.
                    missingPeriods={outstandingPeriods}
                />
            )}
        </>
    );
}
